import qs from 'query-string'
import $global from '@xt/client/store/global'
import { $service } from '@xt/client/experimental/request/service'
import { reportEntryLog } from '@xt/client/utils/log'
import lodash from 'lodash'
import type { LaunchMiniProgramOptions, WXOpenSDK } from '@xt/client/types/wxopensdk.d'
import { SDKErrCode } from '@xt/client/enums/mini'

export type MiniProgrmType = 'uc' | 'show' | 'wenfang' | 'daycourse'

export type OpenMiniProgramOptionType = {
  miniProgrmType: MiniProgrmType
  path?: string
  query?: Record<string, any>
}

// 错误码描述映射
const ErrCodeMsg: Record<SDKErrCode, string> = {
  [SDKErrCode.SUCCESS]: '',
  [SDKErrCode.INVALID_ENVIRONMENT]: '当前环境暂不支持该功能',
  [SDKErrCode.NETWORK_ERROR]: '网络连接异常，请检查网络后重试',
  [SDKErrCode.TICKET_ERROR]: '登录状态已失效，请重新进入页面',
  [SDKErrCode.INVALID_PARAMS]: '请求信息异常，请刷新页面后重试',
  [SDKErrCode.SERVER_ERROR]: '系统繁忙，请稍后再试',
  [SDKErrCode.TIMEOUT]: '操作超时，请检查网络后重试',
  [SDKErrCode.UNKNOWN_ERROR]: '出现未知问题，请稍后再试',
  [SDKErrCode.RATE_LIMIT_EXCEEDED]: '操作过于频繁，请稍后再试',
  [SDKErrCode.INVALID_API_NAME]: '系统配置异常，请稍后再试',
  [SDKErrCode.TICKET_ERROR_V2]: '登录状态已失效，请重新进入页面',
  [SDKErrCode.INVALID_DOMAIN]: '当前页面暂不支持该功能',
  [SDKErrCode.HTTPS_REQUIRED]: '当前页面暂不支持该功能',
  [SDKErrCode.PERMISSION_DENIED]: '当前账号暂未开通该功能',
  [SDKErrCode.INVALID_REQUEST_METHOD]: '系统请求异常，请刷新页面后重试',
  [SDKErrCode.CLIENT_VERSION_NOT_SUPPORTED]: '当前微信版本过低，请升级后再试'
}

const appids: Record<MiniProgrmType, string> = {
  show: 'gh_08398972343c', // 暄桐展览
  wenfang: 'gh_79c64ca9ccaf', // 暄桐文房
  daycourse: 'gh_6df4e6c6d0bc', // 暄桐日课
  uc: 'gh_2f751118256c' // 暄桐
}

declare global {
  interface Window {
    wxopensdk?: WXOpenSDK
  }
}

export type Result = {
  success: boolean
  reason: string
  errcodeDesc?: string
}

// 根据错误码获取对应的错误信息
function getErrMsg(errcode: SDKErrCode): string {
  return ErrCodeMsg[errcode] ?? ErrCodeMsg[SDKErrCode.UNKNOWN_ERROR]
}

function ensureLeadingSlash(path: string): `/${string}` | '' {
  return typeof path !== 'string' || path === '' ? '' : path.startsWith('/') ? (path as `/${string}`) : `/${path}`
}

/**
 *
 * @param type 要打开的小程序
 * @param path 小程序启动路径
 * @param query 小程序页面参数
 * @param timeout 超时时间
 */
export async function openMiniProgram(
  type: MiniProgrmType,
  path: string,
  query: Record<string, any> = {},
  timeout: number = 5000
): Promise<Result> {
  const params = qs.stringify(query)

  if (ensureLeadingSlash(path) && params) {
    path += ensureLeadingSlash(path).indexOf('?') === -1 ? `?${params}` : `&=${params}`
  }

  console.warn(`小程序路径已经准备好: ${path}`)

  if ($global.platform !== 'PC') {
    reportEntryLog('system.open-mini.fail', { reason: '平台错误', platform: $global.platform, path })
    return {
      success: false,
      reason: '不支持的平台调用'
    }
  }

  if (!window.wxopensdk) {
    reportEntryLog('system.open-mini.fail', { reason: '找不到sdk实例', path })
    return {
      success: false,
      reason: '找不到sdk实例'
    }
  }

  // 获取本次接口需要的ticket
  const [err, ticket] = await $service<string>({
    url: `/uc/api/wxmp/opensdk/ticket`
  })

  if (err) {
    reportEntryLog('system.open-mini.get-ticket-fail', { err, path })
    return { success: false, reason: err.errMsg }
  }

  console.warn(`wxopensdk 是否ready: ${window.wxopensdk.ready} ticket = ${ticket}`)
  const options = { appid: $global.openAppid!, userName: appids[type], path, ticket, timeout } satisfies LaunchMiniProgramOptions

  if (window.wxopensdk.ready) {
    const { errcode, errmsg, actionId } = await window.wxopensdk.launchMiniProgram(options)

    console.log({
      errcode,
      errmsg,
      actionId
    })

    if (errcode === 0) {
      return { success: true, reason: '' }
    }

    reportEntryLog('system.open-mini.fail', {
      errcode,
      errmsg,
      actionId,
      ...lodash.omit(options, 'ticket')
    })

    return { success: false, reason: getErrMsg(errcode) }
  }

  return new Promise<Result>(resolve => {
    window.wxopensdk!.onReady = async () => {
      const { errcode, errmsg, actionId } = await window.wxopensdk!.launchMiniProgram(options)

      if (errcode === 0) {
        resolve({ success: true, reason: '' })
      } else {
        reportEntryLog('system.open-mini.fail', {
          errcode,
          errmsg,
          actionId,
          pos: 'onReady',
          ...lodash.omit(options, 'ticket')
        })
        resolve({ success: false, reason: getErrMsg(errcode) })
      }
    }
  })
}
